/**
 * Dynamic Google Fonts Loader
 *
 * Loads fonts on-demand from Google Fonts CDN based on marketplace theme.
 * Only the fonts actually used by the marketplace are loaded.
 */

// Map of font names to their Google Fonts API names
const FONT_MAP: Record<string, string> = {
  Inter: 'Inter:wght@100;200;300;400;500;600;700;800;900',
  'Open Sans': 'Open+Sans:wght@300;400;500;600;700;800',
  Lato: 'Lato:wght@100;300;400;700;900',
  Merriweather: 'Merriweather:wght@300;400;700;900',
  Montserrat: 'Montserrat:wght@100;200;300;400;500;600;700;800;900',
  Poppins: 'Poppins:wght@100;200;300;400;500;600;700;800;900',
  Raleway: 'Raleway:wght@100;200;300;400;500;600;700;800;900',
  Roboto: 'Roboto:wght@100;300;400;500;700;900',
  'Roboto Slab': 'Roboto+Slab:wght@100;200;300;400;500;600;700;800;900',
  'Source Sans Pro': 'Source+Sans+Pro:wght@200;300;400;600;700;900',
  'Bebas Neue': 'Bebas+Neue',
  Gelasio: 'Gelasio:wght@400;500;600;700',
  'Space Grotesk': 'Space+Grotesk:wght@300;400;500;600;700',
  'DM Sans': 'DM+Sans:wght@400;500;700',
  'Playfair Display': 'Playfair+Display:wght@400;500;600;700;800;900',
  'Playfair Display Variable': 'Playfair+Display:wght@400;500;600;700;800;900',
  Manrope: 'Manrope:wght@200;300;400;500;600;700;800',
  'Manrope Variable': 'Manrope:wght@200;300;400;500;600;700;800',
  Orbitron: 'Orbitron:wght@400;500;600;700;800;900',
  Play: 'Play:wght@400;700',
  Oxanium: 'Oxanium:wght@200;300;400;500;600;700;800',
  Silkscreen: 'Silkscreen:wght@400;700',
  Salsa: 'Salsa',
  'Special Elite': 'Special+Elite',
  VT323: 'VT323',
  'Averia Serif Libre': 'Averia+Serif+Libre:wght@300;400;700',
};

// Track which fonts are already loaded to avoid duplicates
const loadedFonts = new Set<string>();

/**
 * Load a Google Font dynamically (client-side only)
 * Returns the CSS font-family string to use
 * Note: This function is safe for SSR but font loading only happens client-side
 */
export const loadGoogleFont = (fontName: string | null | undefined): string => {
  if (!fontName) {
    return "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  }

  // Return font family immediately
  const fontFamily = `'${fontName}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

  // Only attempt to load fonts in browser environment
  if (typeof window === 'undefined') {
    return fontFamily;
  }

  // Only load if not already loaded and font exists
  if (!loadedFonts.has(fontName) && FONT_MAP[fontName]) {
    const googleFontName = FONT_MAP[fontName];

    // Create link element for Google Fonts with display=swap to prevent FOUT
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${googleFontName}&display=swap`;

    // Add to head
    document.head.appendChild(link);

    // Mark as loaded
    loadedFonts.add(fontName);

    if (process.env.NODE_ENV === 'development') {
      console.log(`[Fonts] Loaded: ${fontName}`);
    }
  } else if (!FONT_MAP[fontName]) {
    console.warn(`[Fonts] Font "${fontName}" not available, using fallback`);
  }

  return fontFamily;
};

/**
 * Preload fonts (call this early in the render to reduce layout shift)
 */
export const preloadGoogleFonts = (...fontNames: (string | null | undefined)[]) => {
  const filteredFonts = fontNames.filter((f): f is string => Boolean(f));
  const uniqueFonts = Array.from(new Set(filteredFonts));

  uniqueFonts.forEach((fontName) => {
    if (fontName && FONT_MAP[fontName] && !loadedFonts.has(fontName)) {
      const googleFontName = FONT_MAP[fontName];

      // Create preload link for faster loading
      const preloadLink = document.createElement('link');
      preloadLink.rel = 'preload';
      preloadLink.as = 'style';
      preloadLink.href = `https://fonts.googleapis.com/css2?family=${googleFontName}&display=swap`;
      document.head.appendChild(preloadLink);

      // Create actual stylesheet link
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = `https://fonts.googleapis.com/css2?family=${googleFontName}&display=swap`;
      document.head.appendChild(link);

      loadedFonts.add(fontName);

      if (process.env.NODE_ENV === 'development') {
        console.log(`[Fonts] Preloaded: ${fontName}`);
      }
    }
  });
};

/**
 * Get font family string without loading (for SSR compatibility)
 */
export const getFontFamily = (fontName: string | null | undefined): string => {
  if (!fontName) {
    return "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  }
  return `'${fontName}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
};

/**
 * Check if a font is supported
 */
export const isFontSupported = (fontName: string): boolean => {
  return fontName in FONT_MAP;
};

/**
 * Get list of all available fonts
 */
export const getAvailableFonts = (): string[] => {
  return Object.keys(FONT_MAP);
};

// Default font family for fallback
export const defaultFontFamily =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
